import { Buffer } from 'buffer';

// Encryption key (in production, this should be generated securely)
const ENCRYPTION_KEY = new Uint8Array(32); // 256-bit key
window.crypto.getRandomValues(ENCRYPTION_KEY);

// Cookie utility functions
export const setCookie = (name, value, options = {}) => {
  // Different settings for development vs production
  const isHttps = window.location.protocol === 'https:';

  // Default secure options - always secure
  const defaultOptions = {
    path: '/',
    secure: isHttps,
    sameSite: 'strict',
  };

  const finalOptions = { ...defaultOptions, ...options };

  let cookieString = `${name}=${encodeURIComponent(value)}`;

  if (finalOptions.path) cookieString += `; path=${finalOptions.path}`;
  if (finalOptions.secure) cookieString += '; secure';
  if (finalOptions.sameSite) cookieString += `; samesite=${finalOptions.sameSite}`;
  if (finalOptions.maxAge) cookieString += `; max-age=${finalOptions.maxAge}`;
  if (finalOptions.expires) cookieString += `; expires=${finalOptions.expires}`;

  document.cookie = cookieString;
};

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop().split(';').shift());
  }
  return null;
};

export const deleteCookie = (name) => {
  const secureAttribute = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${secureAttribute}; samesite=strict`;
};

// Secure storage implementation with proper cookie handling
export const secureStorage = {
  // Access token (short-lived)
  setToken: (token) => {
    const trimmedToken = token ? token.trim() : token;
    setCookie('kc_access_token', trimmedToken);
  },
  getToken: () => {
    return getCookie('kc_access_token');
  },
  clearToken: () => {
    deleteCookie('kc_access_token');
  },

  // Refresh token
  setRefreshToken: (token) => {
    const trimmedToken = token ? token.trim() : token;
    setCookie('kc_refresh_token', trimmedToken);
  },
  getRefreshToken: () => {
    return getCookie('kc_refresh_token');
  },
  clearRefreshToken: () => {
    deleteCookie('kc_refresh_token');
  },

  // ID token (longer-lived)
  setIdToken: (token) => {
    const trimmedToken = token ? token.trim() : token;
    setCookie('kc_id_token', trimmedToken);
  },
  getIdToken: () => {
    return getCookie('kc_id_token');
  },
  clearIdToken: () => {
    deleteCookie('kc_id_token');
  },

  // Clear all tokens
  clearAllTokens: () => {
    secureStorage.clearToken();
    secureStorage.clearRefreshToken();
    secureStorage.clearIdToken();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('currentProjectId');
    }
  }
};

// Data encryption using Web Crypto API
export const encryptData = async (data) => {
  try {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encodedData = new TextEncoder().encode(JSON.stringify(data));

    const key = await window.crypto.subtle.importKey(
      'raw',
      ENCRYPTION_KEY,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );

    const encryptedData = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encodedData
    );

    return {
      iv: Buffer.from(iv).toString('base64'),
      data: Buffer.from(encryptedData).toString('base64')
    };
  } catch (error) {
    console.error('Encryption failed:', error);
    return null;
  }
};

// Data decryption using Web Crypto API
export const decryptData = async (encryptedData) => {
  try {
    const iv = Buffer.from(encryptedData.iv, 'base64');
    const data = Buffer.from(encryptedData.data, 'base64');

    const key = await window.crypto.subtle.importKey(
      'raw',
      ENCRYPTION_KEY,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    const decryptedData = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    return JSON.parse(new TextDecoder().decode(decryptedData));
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
};

// Secure redirect URL validation
export const validateRedirectUrl = (url) => {
  try {
    const redirectUrl = new URL(url, window.location.origin);
    return redirectUrl.origin === window.location.origin;
  } catch {
    return false;
  }
};

// CSRF token generation and validation
export const generateCsrfToken = () => {
  const token = window.crypto.getRandomValues(new Uint8Array(32));
  return Buffer.from(token).toString('base64');
};

export const setCsrfToken = (token) => {
  document.cookie = `csrf_token=${token}; path=/; secure; samesite=strict`;
};

export const getCsrfToken = () => {
  return document.cookie.match(/csrf_token=([^;]+)/)?.[1];
};
